import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, Copy, Check, Loader2, KeyRound, Download, Printer } from "lucide-react";
import { createVault } from "@/lib/vault/vaultClient";
import { useToast } from "@/hooks/use-toast";

interface Props {
  open: boolean;
  onCreated: () => void;
}

type Phase = "password" | "recovery";

export default function VaultCreateModal({ open, onCreated }: Props) {
  const { toast } = useToast();
  const [phase, setPhase] = useState<Phase>("password");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [recoveryPhrase, setRecoveryPhrase] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCreate = async () => {
    setError(null);
    if (password.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const { recoveryPhrase } = await createVault(password);
      setRecoveryPhrase(recoveryPhrase);
      setPhase("recovery");
    } catch (err: any) {
      setError(err?.message ?? "Could not create vault.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!recoveryPhrase) return;
    try {
      await navigator.clipboard.writeText(recoveryPhrase);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Please write the phrase down by hand.", variant: "destructive" });
    }
  };

  const handleDownload = () => {
    if (!recoveryPhrase) return;
    const lines = [
      "ForgeRestore — Vault Recovery Phrase",
      "==================================",
      "",
      "These 24 words are the ONLY way to unlock your encrypted vault",
      "if you forget your password. ForgeRestore does not store them and",
      "cannot recover them for you. Keep this file offline and private.",
      "",
      ...recoveryPhrase.split(" ").map((w, i) => `${String(i + 1).padStart(2, " ")}. ${w}`),
      "",
      `Generated: ${new Date().toISOString()}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ForgeRestore-vault-recovery-phrase.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handlePrint = () => {
    if (!recoveryPhrase) return;
    const words = recoveryPhrase.split(" ");
    const html = `<!doctype html><html><head><title>ForgeRestore Recovery Phrase</title>
<style>
  body { font-family: ui-monospace, monospace; padding: 2rem; color: #000; }
  h1 { font-size: 1.25rem; margin: 0 0 0.5rem; }
  p { font-size: 0.85rem; line-height: 1.45; max-width: 36rem; }
  ol { columns: 3; column-gap: 2rem; font-size: 1rem; padding-left: 1.5rem; margin-top: 1.25rem; }
  li { margin: 0.25rem 0; break-inside: avoid; }
  small { color: #666; font-size: 0.75rem; }
</style></head><body>
  <h1>ForgeRestore — Vault Recovery Phrase</h1>
  <p>These 24 words are the only way to unlock your encrypted vault if you forget
  your password. ForgeRestore does not store them and cannot recover them for you.
  Keep this page somewhere safe and offline.</p>
  <ol>${words.map((w) => `<li>${w}</li>`).join("")}</ol>
  <small>Generated: ${new Date().toLocaleString()}</small>
  <script>window.onload = () => { window.print(); };</script>
</body></html>`;
    const w = window.open("", "_blank", "noopener,noreferrer,width=600,height=800");
    if (!w) {
      toast({ title: "Pop-up blocked", description: "Allow pop-ups to print, or use Download instead.", variant: "destructive" });
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handleDone = () => {
    onCreated();
  };

  return (
    <Dialog open={open}>
      <DialogContent className="max-w-lg" data-testid="dialog-vault-create">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Set up your encryption vault
          </DialogTitle>
          <DialogDescription>
            Your personal recovery content stays encrypted on your device.
            Only you — with your password or recovery phrase — can unlock it.
          </DialogDescription>
        </DialogHeader>

        {phase === "password" && (
          <div className="space-y-4">
            <Alert>
              <AlertDescription className="text-sm">
                This password is <strong>separate from your sign-in</strong> and is never sent to ForgeRestore.
                If you lose <strong>both</strong> this password and your 24-word recovery phrase,
                your encrypted content — journal entries, step work, crisis plans, messages, and more —
                is <strong>permanently unreadable</strong>. No one, including ForgeRestore, can recover it.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="vault-pwd">Vault password</Label>
              <Input
                id="vault-pwd"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="input-vault-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vault-pwd-confirm">Confirm password</Label>
              <Input
                id="vault-pwd-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="input-vault-password-confirm"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive" data-testid="text-vault-create-error">{error}</p>
            )}

            <DialogFooter>
              <Button
                onClick={handleCreate}
                disabled={submitting}
                data-testid="button-create-vault"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                {submitting ? "Generating keys…" : "Create vault"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === "recovery" && recoveryPhrase && (
          <div className="space-y-4">
            <Alert>
              <AlertDescription className="text-sm">
                Write these <strong>24 words</strong> down somewhere safe and offline.
                This is the <strong>only way</strong> to unlock your vault if you forget your password.
                ForgeRestore has no copy and cannot recover them for you.
              </AlertDescription>
            </Alert>

            <div
              className="grid grid-cols-3 gap-2 p-4 bg-muted rounded-md font-mono text-sm"
              data-testid="text-recovery-phrase"
            >
              {recoveryPhrase.split(" ").map((w, i) => (
                <div key={i} className="flex items-baseline gap-1">
                  <span className="text-muted-foreground text-xs">{i + 1}.</span>
                  <span>{w}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
                data-testid="button-copy-recovery"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? "Copied" : "Copy"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                data-testid="button-download-recovery"
              >
                <Download className="w-4 h-4 mr-2" />
                Download .txt
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                data-testid="button-print-recovery"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>

            <div className="flex items-start gap-2 pt-2">
              <Checkbox
                id="ack-recovery"
                checked={acknowledged}
                onCheckedChange={(c) => setAcknowledged(c === true)}
                data-testid="checkbox-ack-recovery"
              />
              <Label htmlFor="ack-recovery" className="text-sm font-normal cursor-pointer">
                I've saved these 24 words somewhere safe. I understand that if I lose them and my vault password, my encrypted content is gone for good — ForgeRestore cannot recover it for me.
              </Label>
            </div>

            <DialogFooter>
              <Button
                onClick={handleDone}
                disabled={!acknowledged}
                data-testid="button-recovery-done"
              >
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
